<template>
  <!-- The look of the resume: template, accent colour, font, text size and spacing. Everything here is saved with the resume (settings, and the
       template), and the preview beside it changes as you choose. Which sections show, and in what order, is the Section order form. -->
  <div class="space-y-5">
    <section>
      <h3 :class="LABEL">Template</h3>
      <TemplatePicker :model-value="template" :accent="settings.accent" columns="grid-cols-3 sm:grid-cols-4" @update:model-value="$emit('template', $event)" />
    </section>

    <section>
      <h3 :class="LABEL">Accent colour</h3>
      <div class="flex flex-wrap items-center gap-2">
        <button v-for="color in SWATCHES" :key="color" type="button" @click="settings.accent = color" :aria-label="`Use ${color}`" :aria-pressed="settings.accent === color"
          class="w-6 h-6 rounded-full ring-offset-2 ring-offset-white dark:ring-offset-[#1C2128] transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          :class="settings.accent === color ? 'ring-2 ring-gray-900 dark:ring-white' : 'hover:ring-2 hover:ring-gray-300'" :style="{ backgroundColor: color }"></button>
        <label class="relative w-6 h-6 rounded-full overflow-hidden border border-dashed border-gray-300 dark:border-gray-600 cursor-pointer" title="Pick any colour">
          <span class="sr-only">Custom colour</span>
          <input type="color" :value="settings.accent" @input="settings.accent = $event.target.value" class="absolute -inset-2 w-10 h-10 cursor-pointer opacity-0">
          <svg class="w-full h-full p-1 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
        </label>
      </div>
    </section>

    <section>
      <h3 :class="LABEL">Font</h3>
      <SegmentedControl v-model="settings.font" label="Font" :options="FONT_OPTIONS" />
    </section>

    <div class="grid grid-cols-2 gap-3">
      <section>
        <h3 :class="LABEL">Text size</h3>
        <SegmentedControl v-model="settings.font_size" label="Text size" :options="SIZE_OPTIONS" />
      </section>
      <section>
        <h3 :class="LABEL">Spacing</h3>
        <SegmentedControl v-model="settings.spacing" label="Spacing" :options="SPACING_OPTIONS" />
      </section>
    </div>

    <div class="pt-1">
      <button type="button" @click="resetLook" class="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white">Reset look</button>
    </div>
  </div>
</template>

<script setup>
import TemplatePicker from '../TemplatePicker.vue'
import SegmentedControl from './SegmentedControl.vue'

const LABEL = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5'
const SWATCHES = ['#2563eb', '#0f766e', '#7c3aed', '#dc2626', '#ea580c', '#111827']
const FONT_OPTIONS = [{ value: 'sans', label: 'Sans' }, { value: 'serif', label: 'Serif' }, { value: 'mono', label: 'Mono' }]
const SIZE_OPTIONS = [{ value: 'small', label: 'S' }, { value: 'medium', label: 'M' }, { value: 'large', label: 'L' }]
const SPACING_OPTIONS = [{ value: 'compact', label: 'Tight' }, { value: 'normal', label: 'Normal' }, { value: 'relaxed', label: 'Airy' }]
const DEFAULTS = { accent: '#2563eb', font: 'sans', font_size: 'medium', spacing: 'normal' } // ResumeContent defaults

// `settings` is the resume's own, edited in place.
const props = defineProps({
  settings: { type: Object, required: true },
  template: { type: String, default: 'modern' },
})
defineEmits(['template'])

const resetLook = () => Object.assign(props.settings, DEFAULTS)
</script>
